import React, { useState, useCallback, useEffect } from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Paper, 
  Grid, 
  Button, 
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
  LinearProgress,
  useTheme,
  useMediaQuery,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import { 
  Analytics as AnalyticsIcon, 
  TrendingUp, 
  TrendingDown, 
  Psychology,
  Timeline,
  Warning,
  CheckCircle,
  Support,
  Speed,
  ShowChart,
  PhotoCamera,
  Upload,
  Info,
  Close
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { logEvent } from 'firebase/analytics';
import { analytics } from '../config/firebase';
import aiAnalysisService from '../services/aiAnalysisService';
import { GeminiAnalysisResponse } from '../config/gemini';
import FeatureGuard from '../components/FeatureGuard';

// Declare TradingView widget for TypeScript
declare global {
  interface Window {
    TradingView: any;
  }
}

interface AnalysisResult extends GeminiAnalysisResponse {
  timestamp: string;
}

const AIAnalysesPage: React.FC = () => {

  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisResult, setAnalysisResult] = useState<AnalysisResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [uploadedImages, setUploadedImages] = useState<string[]>([]);
  const [showMobileTips, setShowMobileTips] = useState(false);
  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const chartContainerRef = React.useRef<HTMLDivElement>(null);
  const tradingViewWidgetRef = React.useRef<any>(null);
  const analysisAbortControllerRef = React.useRef<AbortController | null>(null);
  const isMountedRef = React.useRef(true);



  // Initialize TradingView widget
  const initializeTradingViewWidget = useCallback(() => {
    if (!isMountedRef.current) return;
    
    try {
      // Clean up existing widget
      if (tradingViewWidgetRef.current) {
        try {
          tradingViewWidgetRef.current.remove();
        } catch (e) {
          console.warn('Error removing existing TradingView widget:', e);
        }
        tradingViewWidgetRef.current = null;
      }

      if (window.TradingView) {
        // Get the appropriate container
        const containerId = isMobile ? 'tradingview_chart_mobile' : 'tradingview_chart';
        const container = document.getElementById(containerId);
        
        if (container) {
          try {
            container.innerHTML = '';
          } catch (e) {
            console.warn('Error clearing chart container:', e);
          }

          // Map instrument to proper TradingView symbol format
          const getSymbolForInstrument = (instrument: string) => {
            const symbolMap: { [key: string]: string } = {
              'EURUSD': 'FX_IDC:EURUSD',
              'GBPUSD': 'FX_IDC:GBPUSD',
              'USDJPY': 'FX_IDC:USDJPY',
              'USDCHF': 'FX_IDC:USDCHF',
              'AUDUSD': 'FX_IDC:AUDUSD',
              'USDCAD': 'FX_IDC:USDCAD',
              'NZDUSD': 'FX_IDC:NZDUSD',
              'XAUUSD': 'TVC:GOLD',
              'XAGUSD': 'TVC:SILVER',
              'BTCUSD': 'BITSTAMP:BTCUSD',
              'ETHUSD': 'BITSTAMP:ETHUSD',
            };
            return symbolMap[instrument] || `FX_IDC:${instrument}`;
          };

          // Convert timeframe to TradingView format
          const getTradingViewInterval = (timeframe: string) => {
            const intervalMap: { [key: string]: string } = {
              '1m': '1',
              '5m': '5',
              '15m': '15',
              '30m': '30',
              '1h': '60',
              '4h': '240',
              '1D': 'D',
              '1W': 'W',
            };
            return intervalMap[timeframe] || '60';
          };

          try {
            tradingViewWidgetRef.current = new window.TradingView.widget({
              autosize: true,
              symbol: 'OANDA:XAUUSD', // Default symbol, users can change it
              interval: '60', // Default 1 hour interval
              timezone: 'Etc/UTC',
              theme: 'dark',
              style: '1',
              locale: 'en',
              toolbar_bg: '#0a0e14',
              enable_publishing: false,
              withdateranges: true,
              hide_side_toolbar: isMobile,
              allow_symbol_change: true,
              container_id: containerId,
              studies: [
                'RSI@tv-basicstudies',
                'MACD@tv-basicstudies',
                'BB@tv-basicstudies'
              ],
              loading_screen: { backgroundColor: "#0a0e14" },
              overrides: {
                "paneProperties.background": "#0a0e14",
                "paneProperties.vertGridProperties.color": "rgba(255, 215, 0, 0.1)",
                "paneProperties.horzGridProperties.color": "rgba(255, 215, 0, 0.1)",
                "symbolWatermarkProperties.transparency": 90,
                "scalesProperties.textColor": "#AAA",
                "mainSeriesProperties.candleStyle.wickUpColor": "#4caf50",
                "mainSeriesProperties.candleStyle.wickDownColor": "#f44336",
                "mainSeriesProperties.candleStyle.upColor": "#4caf50",
                "mainSeriesProperties.candleStyle.downColor": "#f44336",
                "mainSeriesProperties.candleStyle.borderUpColor": "#4caf50",
                "mainSeriesProperties.candleStyle.borderDownColor": "#f44336"
              }
            });
          } catch (error) {
            console.error('Error creating TradingView widget:', error);
          }
        }
      }
    } catch (error) {
      console.error('Error in TradingView widget initialization:', error);
    }
  }, [isMobile]);

  // Load TradingView widget script
  useEffect(() => {
    // Check if script already exists
    const existingScript = document.querySelector('script[src="https://s3.tradingview.com/tv.js"]');
    
    if (!existingScript) {
      const script = document.createElement('script');
      script.src = 'https://s3.tradingview.com/tv.js';
      script.async = true;
      script.onload = () => {
        console.log('TradingView script loaded successfully');
        setTimeout(() => {
          initializeTradingViewWidget();
        }, 100);
      };
      script.onerror = () => {
        console.error('Failed to load TradingView script');
        // Handle both desktop and mobile containers
        const desktopContainer = document.getElementById('tradingview_chart');
        const mobileContainer = document.getElementById('tradingview_chart_mobile');
        const errorContent = `
          <div style="display: flex; align-items: center; justify-content: center; height: 500px; background: #0a0e14; border-radius: 8px; color: #aaa; text-align: center; flex-direction: column;">
            <div style="font-size: 18px; margin-bottom: 10px;">📡 Connection Error</div>
            <div>Unable to connect to TradingView. Please check your internet connection.</div>
          </div>
        `;
        if (desktopContainer) {
          desktopContainer.innerHTML = errorContent;
        }
        if (mobileContainer) {
          mobileContainer.innerHTML = errorContent;
        }
      };
      document.head.appendChild(script);
    } else {
      // Script already loaded, initialize widget directly
      setTimeout(() => {
        initializeTradingViewWidget();
      }, 100);
    }

    return () => {
      // Don't remove script on cleanup as it might be used by other components
    };
  }, []);

  // Initialize chart on component mount and track page view
  useEffect(() => {
    // Track page view
    if (analytics) {
      logEvent(analytics, 'page_view', {
        page_title: 'AI Market Analysis',
        page_location: window.location.href,
        page_path: '/ai-analyses'
      });
    }

    if (window.TradingView) {
      const timeout = setTimeout(() => {
        initializeTradingViewWidget();
      }, 300);

      return () => clearTimeout(timeout);
    }
  }, [initializeTradingViewWidget]);

  // AI Analysis function
  const performAIAnalysis = async () => {
    if (!isMountedRef.current) return;
    
    // Track analysis start
    if (analytics) {
      logEvent(analytics, 'ai_analysis_start', {
        analysis_type: 'chart_analysis',
        device_type: isMobile ? 'mobile' : 'desktop'
      });
    }
    
    // Cancel any ongoing analysis
    if (analysisAbortControllerRef.current) {
      analysisAbortControllerRef.current.abort();
    }
    
    analysisAbortControllerRef.current = new AbortController();
    
    setIsAnalyzing(true);
    setError(null);

    try {
      const request = {
        instrument: 'Chart Analysis',
        timeframe: 'Current',
        context: 'Market analysis based on the current chart displayed in TradingView widget'
      };

      // Use real Gemini AI API
      const response = await aiAnalysisService.analyzeChart(request);

      if (!isMountedRef.current || analysisAbortControllerRef.current?.signal.aborted) {
        return; // Component unmounted or operation cancelled
      }

      const analysisWithTimestamp: AnalysisResult = {
        ...response,
        timestamp: new Date().toLocaleString()
      };

      setAnalysisResult(analysisWithTimestamp);

      // Track successful analysis
      if (analytics) {
        logEvent(analytics, 'ai_analysis_complete', {
          analysis_type: 'chart_analysis',
          sentiment: response.sentiment,
          confidence: response.confidence,
          risk_level: response.riskLevel,
          device_type: isMobile ? 'mobile' : 'desktop'
        });
      }
    } catch (err: any) {
      if (!isMountedRef.current || analysisAbortControllerRef.current?.signal.aborted) {
        return; // Component unmounted or operation cancelled
      }
      
      console.error('Analysis error:', err);
      setError(err.message || 'Failed to perform AI analysis. Please try again.');

      // Track analysis error
      if (analytics) {
        logEvent(analytics, 'ai_analysis_error', {
          analysis_type: 'chart_analysis',
          error_message: err.message || 'Unknown error',
          device_type: isMobile ? 'mobile' : 'desktop'
        });
      }
    } finally {
      if (isMountedRef.current) {
        setIsAnalyzing(false);
      }
    }
  };

  const getSentimentIcon = (sentiment: string) => {
    switch (sentiment) {
      case 'bullish': return <TrendingUp sx={{ color: '#4caf50' }} />;
      case 'bearish': return <TrendingDown sx={{ color: '#f44336' }} />;
      default: return <Timeline sx={{ color: '#ff9800' }} />;
    }
  };

  const getRiskColor = (risk: string) => {
    switch (risk) {
      case 'low': return '#4caf50';
      case 'medium': return '#ff9800';
      case 'high': return '#f44336';
      default: return '#757575';
    }
  };

  const getConfidenceColor = (confidence: number) => {
    if (confidence >= 80) return '#4caf50';
    if (confidence >= 60) return '#ff9800';
    return '#f44336';
  };

  // Multiple images upload handler
  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      const newFiles = Array.from(files).slice(0, 3 - uploadedImages.length); // Limit to max 3 total images
      
      if (uploadedImages.length + newFiles.length > 3) {
        setError('Maximum 3 images allowed. Please remove some images first.');
        
        // Track upload error
        if (analytics) {
          logEvent(analytics, 'image_upload_error', {
            error_type: 'max_images_exceeded',
            current_count: uploadedImages.length,
            attempted_count: files.length,
            device_type: isMobile ? 'mobile' : 'desktop'
          });
        }
        return;
      }

      // Track upload start
      if (analytics) {
        logEvent(analytics, 'image_upload_start', {
          file_count: newFiles.length,
          total_images: uploadedImages.length + newFiles.length,
          device_type: isMobile ? 'mobile' : 'desktop'
        });
      }

      // Process each file
      Promise.all(
        newFiles.map((file) => {
          return new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (e) => {
              const base64String = e.target?.result as string;
              resolve(base64String);
            };
            reader.onerror = () => reject(new Error('Failed to read file'));
            reader.readAsDataURL(file);
          });
        })
      ).then((base64Images) => {
        setUploadedImages(prev => [...prev, ...base64Images]);
        setError(null);
        
        // Track successful upload
        if (analytics) {
          logEvent(analytics, 'image_upload_success', {
            file_count: newFiles.length,
            total_images: uploadedImages.length + newFiles.length,
            device_type: isMobile ? 'mobile' : 'desktop'
          });
        }
        
        // Clear the input value to allow re-uploading
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }).catch(() => {
        setError('Failed to read one or more image files. Please try again.');
        
        // Track upload error
        if (analytics) {
          logEvent(analytics, 'image_upload_error', {
            error_type: 'file_read_failed',
            file_count: newFiles.length,
            device_type: isMobile ? 'mobile' : 'desktop'
          });
        }
        
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      });
    }
  };

  // AI Analysis with uploaded images
  const performMobileAIAnalysis = async () => {
    if (!uploadedImages.length || !isMountedRef.current) {
      if (isMountedRef.current) {
        setError('Please upload at least one chart screenshot first');
        
        // Track analysis attempt without images
        if (analytics) {
          logEvent(analytics, 'ai_analysis_error', {
            analysis_type: 'image_analysis',
            error_type: 'no_images_uploaded',
            device_type: isMobile ? 'mobile' : 'desktop'
          });
        }
      }
      return;
    }

    // Track image analysis start
    if (analytics) {
      logEvent(analytics, 'ai_analysis_start', {
        analysis_type: 'image_analysis',
        image_count: uploadedImages.length,
        multiple_images: uploadedImages.length > 1,
        device_type: isMobile ? 'mobile' : 'desktop'
      });
    }

    // Cancel any ongoing analysis
    if (analysisAbortControllerRef.current) {
      analysisAbortControllerRef.current.abort();
    }
    
    analysisAbortControllerRef.current = new AbortController();

    setIsAnalyzing(true);
    setError(null);

    try {
      const imageCount = uploadedImages.length;
      const contextDescription = imageCount > 1 
        ? `Combined analysis of ${imageCount} chart screenshots from TradingView`
        : `Chart screenshot analysis from TradingView`;

      const request = {
        instrument: 'Chart Analysis',
        timeframe: 'Current',
        context: contextDescription,
        imageData: uploadedImages.map(img => img.split(',')[1]), // Remove data:image/png;base64, prefix from each image
        multipleImages: imageCount > 1
      };

      // Call AI service with uploaded images
      const response = await aiAnalysisService.analyzeChartWithImage(request);

      if (!isMountedRef.current || analysisAbortControllerRef.current?.signal.aborted) {
        return; // Component unmounted or operation cancelled
      }

      const analysisWithTimestamp: AnalysisResult = {
        ...response,
        timestamp: new Date().toLocaleString()
      };

      setAnalysisResult(analysisWithTimestamp);

      // Track successful image analysis
      if (analytics) {
        logEvent(analytics, 'ai_analysis_complete', {
          analysis_type: 'image_analysis',
          image_count: imageCount,
          multiple_images: imageCount > 1,
          sentiment: response.sentiment,
          confidence: response.confidence,
          risk_level: response.riskLevel,
          device_type: isMobile ? 'mobile' : 'desktop'
        });
      }
    } catch (err: any) {
      if (!isMountedRef.current || analysisAbortControllerRef.current?.signal.aborted) {
        return; // Component unmounted or operation cancelled
      }
      
      console.error('Analysis error:', err);
      setError(err.message || 'Failed to perform AI analysis. Please try again.');

      // Track image analysis error
      if (analytics) {
        logEvent(analytics, 'ai_analysis_error', {
          analysis_type: 'image_analysis',
          image_count: uploadedImages.length,
          error_message: err.message || 'Unknown error',
          device_type: isMobile ? 'mobile' : 'desktop'
        });
      }
    } finally {
      if (isMountedRef.current) {
        setIsAnalyzing(false);
      }
    }
  };

  // Clear uploaded images
  const clearUploadedImages = () => {
    const imageCount = uploadedImages.length;
    
    // Track clearing images
    if (analytics && imageCount > 0) {
      logEvent(analytics, 'images_cleared', {
        image_count: imageCount,
        device_type: isMobile ? 'mobile' : 'desktop'
      });
    }
    
    setUploadedImages([]);
    setError(null);
    // Clear the file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Remove specific image
  const removeImage = (index: number) => {
    // Track image removal
    if (analytics) {
      logEvent(analytics, 'image_removed', {
        removed_index: index,
        total_images: uploadedImages.length,
        device_type: isMobile ? 'mobile' : 'desktop'
      });
    }
    
    setUploadedImages(prev => prev.filter((_, i) => i !== index));
    setError(null);
  };

  // Cleanup effect
  React.useEffect(() => {
    return () => {
      // Mark component as unmounted
      isMountedRef.current = false;
      
      // Cancel any ongoing analysis
      if (analysisAbortControllerRef.current) {
        analysisAbortControllerRef.current.abort();
      }
      
      // Cleanup TradingView widget
      if (tradingViewWidgetRef.current) {
        try {
          tradingViewWidgetRef.current.remove();
        } catch (e) {
          console.warn('Error removing TradingView widget on cleanup:', e);
        }
        tradingViewWidgetRef.current = null;
      }
      
      // Cleanup file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      
      // Clear state
      setUploadedImages([]);
      setError(null);
      setIsAnalyzing(false);
      setAnalysisResult(null);
    };
  }, []);

  // Error boundary effect
  React.useEffect(() => {
    const handleError = (event: ErrorEvent) => {
      if (event.message.includes('removeChild') && isMountedRef.current) {
        console.warn('Caught removeChild error, resetting component state:', event.message);
        // Reset component state to prevent further errors
        setError(null);
        setIsAnalyzing(false);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        // Prevent the error from propagating
        event.preventDefault();
        return false;
      }
    };

    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      if (event.reason?.message?.includes('removeChild') && isMountedRef.current) {
        console.warn('Caught removeChild promise rejection:', event.reason.message);
        event.preventDefault();
        return false;
      }
    };

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    
    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []);

  return (
    <Box sx={{ minHeight: '100vh', pt: { xs: 12, md: 14 }, pb: 4 }}>
      <Container maxWidth="xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          {/* Header */}
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography 
              variant="h2" 
              component="h1" 
              gutterBottom 
              sx={{ 
                mb: 2,
                background: 'linear-gradient(45deg, #FFD700 30%, #FFF 90%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                fontWeight: 700,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 2
              }}
            >
              <Psychology sx={{ fontSize: 'inherit', color: '#FFD700' }} />
              AI Market Analysis
            </Typography>
            <Typography 
              variant="h6" 
              color="text.secondary" 
              sx={{ mb: 2 }}
            >
              Advanced AI-powered technical analysis with real-time market insights
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {/* Controls Panel */}
            <Grid size={{ xs: 12, md: 4 }}>
              <Paper
                elevation={3}
                sx={{
                  p: 3,
                  backgroundColor: 'rgba(255, 255, 255, 0.02)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 215, 0, 0.2)',
                  borderRadius: 3,
                  height: 'fit-content'
                }}
              >
                <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', mb: 3 }}>
                  <AnalyticsIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Analysis Controls
                </Typography>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 3, textAlign: 'center' }}>
                  💡 Change the symbol and timeframe directly on the TradingView chart
                </Typography>

                {/* Desktop Image Upload Section */}
                {!isMobile && (
                  <Box sx={{ mb: 3, p: 2, border: '1px dashed rgba(255, 215, 0, 0.3)', borderRadius: 2, backgroundColor: 'rgba(255, 215, 0, 0.05)' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <PhotoCamera sx={{ mr: 1, color: 'primary.main' }} />
                      <Typography variant="h6" sx={{ color: 'primary.main', flex: 1 }}>
                        Chart Image Analysis
                      </Typography>
                    </Box>
                    
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      💡 Use the camera icon (📷) in the TradingView chart to download images, then upload them here for analysis (max 3 images)
                    </Typography>

                    {uploadedImages.length > 0 ? (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                          {uploadedImages.length} of 3 images uploaded
                        </Typography>
                        
                        <Box sx={{ 
                          display: 'grid', 
                          gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', 
                          gap: 1, 
                          mb: 2 
                        }}>
                          {uploadedImages.map((image, index) => (
                            <Box 
                              key={index}
                              sx={{ 
                                position: 'relative', 
                                border: '2px solid #4caf50', 
                                borderRadius: 2, 
                                overflow: 'hidden',
                              }}
                            >
                              <img 
                                src={image} 
                                alt={`Uploaded chart ${index + 1}`} 
                                style={{ 
                                  width: '100%', 
                                  height: 'auto', 
                                  maxHeight: '120px', 
                                  objectFit: 'contain' 
                                }} 
                              />
                              <IconButton
                                size="small"
                                onClick={() => removeImage(index)}
                                sx={{
                                  position: 'absolute',
                                  top: 4,
                                  right: 4,
                                  backgroundColor: 'rgba(0, 0, 0, 0.7)',
                                  color: 'white',
                                  width: 24,
                                  height: 24,
                                  '&:hover': {
                                    backgroundColor: 'rgba(0, 0, 0, 0.9)',
                                  }
                                }}
                              >
                                <Close sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Box>
                          ))}
                        </Box>
                        
                        <Box sx={{ display: 'flex', gap: 1, flexDirection: 'column' }}>
                          <Button
                            fullWidth
                            variant="contained"
                            onClick={performMobileAIAnalysis}
                            disabled={isAnalyzing}
                            sx={{
                              background: 'linear-gradient(45deg, #4caf50 30%, #2e7d32 90%)',
                              color: '#fff',
                              fontWeight: 'bold',
                              '&:hover': {
                                background: 'linear-gradient(45deg, #2e7d32 30%, #4caf50 90%)',
                              }
                            }}
                          >
                            {isAnalyzing ? (
                              <>
                                <CircularProgress size={20} sx={{ mr: 1, color: '#fff' }} />
                                Analyzing {uploadedImages.length} Image{uploadedImages.length > 1 ? 's' : ''}...
                              </>
                            ) : (
                              <>
                                <Psychology sx={{ mr: 1 }} />
                                Analyze {uploadedImages.length} Image{uploadedImages.length > 1 ? 's' : ''}
                              </>
                            )}
                          </Button>
                          
                          <Button
                            fullWidth
                            variant="outlined"
                            onClick={clearUploadedImages}
                            size="small"
                            sx={{ 
                              color: 'error.main',
                              borderColor: 'error.main',
                              '&:hover': {
                                borderColor: 'error.dark',
                                backgroundColor: 'rgba(244, 67, 54, 0.1)',
                              }
                            }}
                          >
                            Clear All Images
                          </Button>
                        </Box>
                      </Box>
                    ) : null}

                    {/* Upload button - always show if less than 3 images */}
                    {uploadedImages.length < 3 && (
                      <Box sx={{ mt: uploadedImages.length > 0 ? 2 : 0 }}>
                        <input
                          accept="image/*"
                          style={{ display: 'none' }}
                          id="desktop-chart-image-upload"
                          type="file"
                          onChange={handleImageUpload}
                          ref={fileInputRef}
                          multiple
                        />
                        <label htmlFor="desktop-chart-image-upload">
                          <Button
                            fullWidth
                            component="span"
                            variant="outlined"
                            sx={{
                              border: '2px dashed rgba(255, 215, 0, 0.5)',
                              color: 'primary.main',
                              py: 2,
                              '&:hover': {
                                border: '2px dashed rgba(255, 215, 0, 0.8)',
                                backgroundColor: 'rgba(255, 215, 0, 0.1)',
                              }
                            }}
                          >
                            <Upload sx={{ mr: 1 }} />
                            {uploadedImages.length > 0 ? 'Add More Images' : 'Upload Chart Images'} ({3 - uploadedImages.length} remaining)
                          </Button>
                        </label>
                      </Box>
                    )}
                  </Box>
                )}

                {/* Mobile Screenshot Upload Section */}
                {isMobile && (
                  <Box sx={{ mt: 3, p: 2, border: '1px dashed rgba(255, 215, 0, 0.3)', borderRadius: 2, backgroundColor: 'rgba(255, 215, 0, 0.05)' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <PhotoCamera sx={{ mr: 1, color: 'primary.main' }} />
                      <Typography variant="h6" sx={{ color: 'primary.main', flex: 1 }}>
                        Mobile Option
                      </Typography>
                      <IconButton 
                        size="small" 
                        onClick={() => {
                          setShowMobileTips(true);
                          // Track mobile tips dialog open
                          if (analytics) {
                            logEvent(analytics, 'mobile_tips_opened', {
                              device_type: isMobile ? 'mobile' : 'desktop'
                            });
                          }
                        }}
                        sx={{ color: 'primary.main' }}
                      >
                        <Info />
                      </IconButton>
                    </Box>
                    
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Take screenshots of your charts and upload them for analysis (max 3 images)
                    </Typography>

                    {uploadedImages.length > 0 ? (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                          {uploadedImages.length} of 3 images uploaded
                        </Typography>
                        
                        <Box sx={{ 
                          display: 'grid', 
                          gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', 
                          gap: 1, 
                          mb: 2 
                        }}>
                          {uploadedImages.map((image, index) => (
                            <Box 
                              key={index}
                              sx={{ 
                                position: 'relative', 
                                border: '2px solid #4caf50', 
                                borderRadius: 2, 
                                overflow: 'hidden',
                              }}
                            >
                              <img 
                                src={image} 
                                alt={`Screenshot ${index + 1}`} 
                                style={{ 
                                  width: '100%', 
                                  height: 'auto', 
                                  maxHeight: '100px', 
                                  objectFit: 'contain' 
                                }} 
                              />
                              <IconButton
                                size="small"
                                onClick={() => removeImage(index)}
                                sx={{
                                  position: 'absolute',
                                  top: 4,
                                  right: 4,
                                  backgroundColor: 'rgba(0, 0, 0, 0.7)',
                                  color: 'white',
                                  width: 20,
                                  height: 20,
                                  '&:hover': {
                                    backgroundColor: 'rgba(0, 0, 0, 0.9)',
                                  }
                                }}
                              >
                                <Close sx={{ fontSize: 14 }} />
                              </IconButton>
                            </Box>
                          ))}
                        </Box>
                        
                        <Box sx={{ display: 'flex', gap: 1, flexDirection: 'column' }}>
                          <Button
                            fullWidth
                            variant="contained"
                            onClick={performMobileAIAnalysis}
                            disabled={isAnalyzing}
                            sx={{
                              background: 'linear-gradient(45deg, #4caf50 30%, #2e7d32 90%)',
                              color: '#fff',
                              fontWeight: 'bold',
                              '&:hover': {
                                background: 'linear-gradient(45deg, #2e7d32 30%, #4caf50 90%)',
                              }
                            }}
                          >
                            {isAnalyzing ? (
                              <>
                                <CircularProgress size={20} sx={{ mr: 1, color: '#fff' }} />
                                Analyzing {uploadedImages.length} Screenshot{uploadedImages.length > 1 ? 's' : ''}...
                              </>
                            ) : (
                              <>
                                <Psychology sx={{ mr: 1 }} />
                                Analyze {uploadedImages.length} Screenshot{uploadedImages.length > 1 ? 's' : ''}
                              </>
                            )}
                          </Button>
                          
                          <Button
                            fullWidth
                            variant="outlined"
                            onClick={clearUploadedImages}
                            size="small"
                            sx={{ 
                              color: 'error.main',
                              borderColor: 'error.main',
                              '&:hover': {
                                borderColor: 'error.dark',
                                backgroundColor: 'rgba(244, 67, 54, 0.1)',
                              }
                            }}
                          >
                            Clear All Images
                          </Button>
                        </Box>
                      </Box>
                    ) : null}

                    {/* Upload button - always show if less than 3 images */}
                    {uploadedImages.length < 3 && (
                      <Box sx={{ mt: uploadedImages.length > 0 ? 2 : 0 }}>
                        <input
                          accept="image/*"
                          style={{ display: 'none' }}
                          id="chart-screenshot-upload"
                          type="file"
                          onChange={handleImageUpload}
                          ref={fileInputRef}
                          multiple
                        />
                        <label htmlFor="chart-screenshot-upload">
                          <Button
                            fullWidth
                            component="span"
                            variant="outlined"
                            sx={{
                              border: '2px dashed rgba(255, 215, 0, 0.5)',
                              color: 'primary.main',
                              py: 2,
                              '&:hover': {
                                border: '2px dashed rgba(255, 215, 0, 0.8)',
                                backgroundColor: 'rgba(255, 215, 0, 0.1)',
                              }
                            }}
                          >
                            <Upload sx={{ mr: 1 }} />
                            {uploadedImages.length > 0 ? 'Add More Screenshots' : 'Upload Chart Screenshots'} ({3 - uploadedImages.length} remaining)
                          </Button>
                        </label>
                      </Box>
                    )}
                  </Box>
                )}

                {error && (
                  <Alert severity="error" sx={{ mt: 2 }}>
                    {error}
                  </Alert>
                )}

                {/* Mobile Tips Dialog */}
                <Dialog
                  open={showMobileTips}
                  onClose={() => {
                    setShowMobileTips(false);
                    // Track mobile tips dialog close
                    if (analytics) {
                      logEvent(analytics, 'mobile_tips_closed', {
                        device_type: isMobile ? 'mobile' : 'desktop'
                      });
                    }
                  }}
                  maxWidth="sm"
                  fullWidth
                  PaperProps={{
                    sx: {
                      backgroundColor: 'rgba(10, 14, 20, 0.95)',
                      backdropFilter: 'blur(10px)',
                      border: '1px solid rgba(255, 215, 0, 0.3)',
                    }
                  }}
                >
                  <DialogTitle sx={{ color: 'primary.main', borderBottom: '1px solid rgba(255, 215, 0, 0.2)' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <PhotoCamera sx={{ mr: 1 }} />
                      📱 Mobile Chart Analysis Tips
                    </Box>
                  </DialogTitle>
                  <DialogContent sx={{ pt: 3 }}>
                    <Typography variant="h6" gutterBottom sx={{ color: 'primary.main' }}>
                      How to Take Chart Screenshots:
                    </Typography>
                    
                    <List>
                      <ListItem sx={{ pl: 0 }}>
                        <ListItemText
                          primary="📱 iPhone/iPad"
                          secondary="Press Volume Up + Power button simultaneously"
                          sx={{ 
                            '& .MuiListItemText-primary': { fontWeight: 'bold', color: 'text.primary' },
                            '& .MuiListItemText-secondary': { color: 'text.secondary' }
                          }}
                        />
                      </ListItem>
                      <ListItem sx={{ pl: 0 }}>
                        <ListItemText
                          primary="🤖 Android"
                          secondary="Press Volume Down + Power button simultaneously"
                          sx={{ 
                            '& .MuiListItemText-primary': { fontWeight: 'bold', color: 'text.primary' },
                            '& .MuiListItemText-secondary': { color: 'text.secondary' }
                          }}
                        />
                      </ListItem>
                    </List>

                    <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', mt: 3 }}>
                      Best Results Tips:
                    </Typography>
                    
                    <List>
                      <ListItem sx={{ pl: 0 }}>
                        <ListItemText
                          primary="✅ Include price axis and time axis"
                          secondary="Make sure the price levels and timestamps are visible"
                          sx={{ 
                            '& .MuiListItemText-primary': { color: 'text.primary' },
                            '& .MuiListItemText-secondary': { color: 'text.secondary' }
                          }}
                        />
                      </ListItem>
                      <ListItem sx={{ pl: 0 }}>
                        <ListItemText
                          primary="✅ Capture technical indicators"
                          secondary="Include RSI, MACD, or other indicators if visible"
                          sx={{ 
                            '& .MuiListItemText-primary': { color: 'text.primary' },
                            '& .MuiListItemText-secondary': { color: 'text.secondary' }
                          }}
                        />
                      </ListItem>
                      <ListItem sx={{ pl: 0 }}>
                        <ListItemText
                          primary="✅ Good image quality"
                          secondary="Ensure text and lines are clear and readable"
                          sx={{ 
                            '& .MuiListItemText-primary': { color: 'text.primary' },
                            '& .MuiListItemText-secondary': { color: 'text.secondary' }
                          }}
                        />
                      </ListItem>
                    </List>
                  </DialogContent>
                  <DialogActions sx={{ borderTop: '1px solid rgba(255, 215, 0, 0.2)', pt: 2 }}>
                    <Button 
                      onClick={() => {
                        setShowMobileTips(false);
                        // Track mobile tips dialog close via button
                        if (analytics) {
                          logEvent(analytics, 'mobile_tips_acknowledged', {
                            device_type: isMobile ? 'mobile' : 'desktop'
                          });
                        }
                      }}
                      variant="contained"
                      sx={{ 
                        background: 'linear-gradient(45deg, #FFD700 30%, #FFA000 90%)',
                        color: '#000',
                        fontWeight: 'bold'
                      }}
                    >
                      Got it!
                    </Button>
                  </DialogActions>
                </Dialog>

              </Paper>
            </Grid>

            {/* Chart Panel */}
            <Grid size={{ xs: 12, md: 8 }}>
              {!isMobile ? (
                <Paper
                  elevation={3}
                  sx={{
                    p: 3,
                    backgroundColor: 'rgba(255, 255, 255, 0.02)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255, 215, 0, 0.2)',
                    borderRadius: 3,
                  }}
                >
                  <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', mb: 2 }}>
                    <ShowChart sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Trading Chart
                  </Typography>
                  
                  <Box 
                    ref={chartContainerRef}
                    id="tradingview_chart"
                    sx={{ 
                      height: 500,
                      minHeight: 500,
                      width: '100%',
                      borderRadius: 2,
                      overflow: 'hidden',
                      backgroundColor: '#0a0e14',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      position: 'relative'
                    }} 
                  >
                    {/* Loading state */}
                    <Box sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#aaa',
                      gap: 2
                    }}>
                      <CircularProgress size={40} sx={{ color: '#FFD700' }} />
                      <Typography variant="body2">Loading Chart...</Typography>
                    </Box>
                  </Box>
                </Paper>
              ) : (
                <Paper
                  elevation={3}
                  sx={{
                    p: 3,
                    backgroundColor: 'rgba(255, 255, 255, 0.02)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255, 215, 0, 0.2)',
                    borderRadius: 3,
                  }}
                >
                  <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', mb: 2 }}>
                    <ShowChart sx={{ mr: 1, verticalAlign: 'middle' }} />
                    📱 Trading Chart
                  </Typography>
                  
                  <Box 
                    ref={chartContainerRef}
                    id="tradingview_chart_mobile"
                    sx={{ 
                      height: 400,
                      minHeight: 400,
                      width: '100%',
                      borderRadius: 2,
                      overflow: 'hidden',
                      backgroundColor: '#0a0e14',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      position: 'relative'
                    }} 
                  >
                    {/* Loading state */}
                    <Box sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#aaa',
                      gap: 2
                    }}>
                      <CircularProgress size={40} sx={{ color: '#FFD700' }} />
                      <Typography variant="body2">Loading Chart...</Typography>
                    </Box>
                  </Box>
                  
                  {/* Mobile Instructions */}
                  <Box sx={{ 
                    mt: 2, 
                    p: 2, 
                    borderRadius: 2, 
                    backgroundColor: 'rgba(255, 215, 0, 0.05)',
                    border: '1px solid rgba(255, 215, 0, 0.2)'
                  }}>
                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mb: 1 }}>
                      💡 <strong>Mobile Tip:</strong> Use the camera icon (📷) in the chart above to download an image
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center', display: 'block' }}>
                      Then upload it using the "Mobile Option" section above for AI analysis
                    </Typography>
                  </Box>
                </Paper>
              )}
            </Grid>
          </Grid>

          {/* Analysis Results */}
          {analysisResult && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <Paper
                elevation={3}
                sx={{
                  p: 4,
                  mt: 4,
                  backgroundColor: 'rgba(255, 255, 255, 0.02)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 215, 0, 0.2)',
                  borderRadius: 3,
                }}
              >
                <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', mb: 3 }}>
                  <Psychology sx={{ mr: 1, verticalAlign: 'middle' }} />
                  AI Analysis Results
                </Typography>

                <Grid container spacing={3}>
                  {/* Sentiment Overview */}
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Card sx={{ backgroundColor: 'rgba(255, 255, 255, 0.05)', height: '100%' }}>
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          {getSentimentIcon(analysisResult.sentiment)}
                          <Typography variant="h6" sx={{ ml: 1, textTransform: 'capitalize' }}>
                            {analysisResult.sentiment} Sentiment
                          </Typography>
                        </Box>
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            Confidence: {analysisResult.confidence}%
                          </Typography>
                          <LinearProgress 
                            variant="determinate" 
                            value={analysisResult.confidence}
                            sx={{ 
                              height: 8, 
                              borderRadius: 4,
                              backgroundColor: 'rgba(255,255,255,0.1)',
                              '& .MuiLinearProgress-bar': {
                                backgroundColor: getConfidenceColor(analysisResult.confidence)
                              }
                            }}
                          />
                        </Box>
                        <Chip 
                          label={`Risk: ${analysisResult.riskLevel.toUpperCase()}`}
                          sx={{ 
                            backgroundColor: getRiskColor(analysisResult.riskLevel),
                            color: '#fff',
                            fontWeight: 'bold'
                          }}
                        />
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* Technical Indicators */}
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Card sx={{ backgroundColor: 'rgba(255, 255, 255, 0.05)', height: '100%' }}>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          <Speed sx={{ mr: 1, verticalAlign: 'middle' }} />
                          Technical Indicators
                        </Typography>
                        <List dense>
                          <ListItem>
                            <ListItemText 
                              primary="RSI" 
                              secondary={analysisResult.indicators.rsi}
                            />
                          </ListItem>
                          <ListItem>
                            <ListItemText 
                              primary="MACD" 
                              secondary={analysisResult.indicators.macd}
                            />
                          </ListItem>
                          <ListItem>
                            <ListItemText 
                              primary="Bollinger Bands" 
                              secondary={analysisResult.indicators.bollinger}
                            />
                          </ListItem>
                        </List>
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* Technical Analysis */}
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Card sx={{ backgroundColor: 'rgba(255, 255, 255, 0.05)', height: '100%' }}>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          <AnalyticsIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                          Technical Analysis
                        </Typography>
                        <Typography 
                          variant="body1" 
                          color="text.primary" 
                          sx={{ 
                            lineHeight: 1.8,
                            backgroundColor: 'rgba(255, 255, 255, 0.02)',
                            p: 2,
                            borderRadius: 2,
                            border: '1px solid rgba(255, 215, 0, 0.1)'
                          }}
                        >
                          {analysisResult.analysis}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* Fundamental Analysis */}
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Card sx={{ backgroundColor: 'rgba(255, 255, 255, 0.05)', height: '100%' }}>
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <Psychology sx={{ mr: 1, verticalAlign: 'middle', color: '#9c27b0' }} />
                          <Typography variant="h6">
                            Fundamental Analysis
                          </Typography>
                          <Box sx={{ ml: 'auto' }}>
                            <Chip 
                              label={`${analysisResult.fundamentalAnalysis?.sentiment?.toUpperCase() || 'NEUTRAL'}`}
                              size="small"
                              icon={getSentimentIcon(analysisResult.fundamentalAnalysis?.sentiment || 'neutral')}
                              sx={{ 
                                backgroundColor: analysisResult.fundamentalAnalysis?.sentiment === 'bullish' ? 'rgba(76, 175, 80, 0.2)' :
                                                analysisResult.fundamentalAnalysis?.sentiment === 'bearish' ? 'rgba(244, 67, 54, 0.2)' :
                                                'rgba(255, 152, 0, 0.2)',
                                color: analysisResult.fundamentalAnalysis?.sentiment === 'bullish' ? '#4caf50' :
                                       analysisResult.fundamentalAnalysis?.sentiment === 'bearish' ? '#f44336' : '#ff9800',
                                fontWeight: 'bold'
                              }}
                            />
                          </Box>
                        </Box>

                        {analysisResult.fundamentalAnalysis && (
                          <Box>
                            {/* Economic Factors */}
                            <Box sx={{ mb: 2 }}>
                              <Typography variant="subtitle2" sx={{ color: '#2196f3', mb: 1, fontWeight: 'bold' }}>
                                🏛️ Economic Factors:
                              </Typography>
                              <Typography 
                                variant="body2" 
                                color="text.primary" 
                                sx={{ 
                                  lineHeight: 1.6,
                                  backgroundColor: 'rgba(33, 150, 243, 0.05)',
                                  p: 1.5,
                                  borderRadius: 1,
                                  border: '1px solid rgba(33, 150, 243, 0.1)',
                                  mb: 1
                                }}
                              >
                                {analysisResult.fundamentalAnalysis.economicFactors}
                              </Typography>
                            </Box>

                            {/* Market Drivers */}
                            <Box sx={{ mb: 2 }}>
                              <Typography variant="subtitle2" sx={{ color: '#ff9800', mb: 1, fontWeight: 'bold' }}>
                                🚀 Market Drivers:
                              </Typography>
                              <Typography 
                                variant="body2" 
                                color="text.primary" 
                                sx={{ 
                                  lineHeight: 1.6,
                                  backgroundColor: 'rgba(255, 152, 0, 0.05)',
                                  p: 1.5,
                                  borderRadius: 1,
                                  border: '1px solid rgba(255, 152, 0, 0.1)',
                                  mb: 1
                                }}
                              >
                                {analysisResult.fundamentalAnalysis.marketDrivers}
                              </Typography>
                            </Box>

                            {/* Outlook */}
                            <Box sx={{ mb: 2 }}>
                              <Typography variant="subtitle2" sx={{ color: '#4caf50', mb: 1, fontWeight: 'bold' }}>
                                🔮 Outlook:
                              </Typography>
                              <Typography 
                                variant="body2" 
                                color="text.primary" 
                                sx={{ 
                                  lineHeight: 1.6,
                                  backgroundColor: 'rgba(76, 175, 80, 0.05)',
                                  p: 1.5,
                                  borderRadius: 1,
                                  border: '1px solid rgba(76, 175, 80, 0.1)'
                                }}
                              >
                                {analysisResult.fundamentalAnalysis.outlook}
                              </Typography>
                            </Box>

                            {/* Fundamental Strength */}
                            <Box>
                              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                Fundamental Strength: {analysisResult.fundamentalAnalysis.strength}%
                              </Typography>
                              <LinearProgress 
                                variant="determinate" 
                                value={analysisResult.fundamentalAnalysis.strength}
                                sx={{ 
                                  height: 6, 
                                  borderRadius: 3,
                                  backgroundColor: 'rgba(255,255,255,0.1)',
                                  '& .MuiLinearProgress-bar': {
                                    backgroundColor: getConfidenceColor(analysisResult.fundamentalAnalysis.strength)
                                  }
                                }}
                              />
                            </Box>
                          </Box>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* Key Levels */}
                  {(analysisResult.keyLevels.support.length > 0 || analysisResult.keyLevels.resistance.length > 0) && (
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Card sx={{ backgroundColor: 'rgba(255, 255, 255, 0.05)', height: '100%' }}>
                        <CardContent>
                          <Typography variant="h6" gutterBottom sx={{ color: '#2196f3' }}>
                            <Support sx={{ mr: 1, verticalAlign: 'middle' }} />
                            Key Levels
                          </Typography>
                          
                          {analysisResult.keyLevels.support.length > 0 && (
                            <Box sx={{ mb: 2 }}>
                              <Typography variant="subtitle2" sx={{ color: '#4caf50', mb: 1 }}>
                                Support Levels:
                              </Typography>
                              {analysisResult.keyLevels.support.map((level, index) => (
                                <Chip 
                                  key={index}
                                  label={level}
                                  size="small"
                                  sx={{ mr: 1, mb: 1, backgroundColor: 'rgba(76, 175, 80, 0.3)', color: '#ffffff', fontWeight: 'bold' }}
                                />
                              ))}
                            </Box>
                          )}

                          {analysisResult.keyLevels.resistance.length > 0 && (
                            <Box>
                              <Typography variant="subtitle2" sx={{ color: '#ff9800', mb: 1 }}>
                                Resistance Levels:
                              </Typography>
                              {analysisResult.keyLevels.resistance.map((level, index) => (
                                <Chip 
                                  key={index}
                                  label={level}
                                  size="small"
                                  sx={{ mr: 1, mb: 1, backgroundColor: 'rgba(255, 152, 0, 0.3)', color: '#ffffff', fontWeight: 'bold' }}
                                />
                              ))}
                            </Box>
                          )}
                        </CardContent>
                      </Card>
                    </Grid>
                  )}

                  {/* Entry Points */}
                  <Grid size={{ xs: 12, md: analysisResult.keyLevels.support.length === 0 && analysisResult.keyLevels.resistance.length === 0 ? 6 : 6 }}>
                    <Card sx={{ backgroundColor: 'rgba(255, 255, 255, 0.05)', height: '100%' }}>
                      <CardContent>
                        <Typography variant="h6" gutterBottom sx={{ color: '#4caf50' }}>
                          <CheckCircle sx={{ mr: 1, verticalAlign: 'middle' }} />
                          Entry Points
                        </Typography>
                        <List dense>
                          {analysisResult.entryPoints.map((point, index) => (
                            <ListItem key={index} sx={{ px: 0 }}>
                              <ListItemText 
                                primary={`${index + 1}. ${point}`}
                                sx={{ 
                                  '& .MuiListItemText-primary': {
                                    color: 'text.primary',
                                    fontSize: '0.95rem',
                                    lineHeight: 1.6
                                  }
                                }}
                              />
                            </ListItem>
                          ))}
                        </List>
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* Exit Points */}
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Card sx={{ backgroundColor: 'rgba(255, 255, 255, 0.05)', height: '100%' }}>
                      <CardContent>
                        <Typography variant="h6" gutterBottom sx={{ color: '#ff9800' }}>
                          <Warning sx={{ mr: 1, verticalAlign: 'middle' }} />
                          Exit Strategy
                        </Typography>
                        <List dense>
                          {analysisResult.exitPoints.map((point, index) => (
                            <ListItem key={index} sx={{ px: 0 }}>
                              <ListItemText 
                                primary={`${index + 1}. ${point}`}
                                sx={{ 
                                  '& .MuiListItemText-primary': {
                                    color: 'text.primary',
                                    fontSize: '0.95rem',
                                    lineHeight: 1.6
                                  }
                                }}
                              />
                            </ListItem>
                          ))}
                        </List>
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* Recommendation */}
                  <Grid size={{ xs: 12 }}>
                    <Card sx={{ 
                      backgroundColor: 'rgba(255, 215, 0, 0.1)',
                      border: '2px solid rgba(255, 215, 0, 0.3)',
                      borderRadius: 3
                    }}>
                      <CardContent sx={{ p: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <Psychology sx={{ mr: 1, verticalAlign: 'middle', color: 'primary.main', fontSize: '1.5rem' }} />
                          <Typography variant="h5" sx={{ color: 'primary.main', fontWeight: 700 }}>
                            AI Trading Recommendation
                          </Typography>
                          <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Chip 
                              label={`${analysisResult.confidence}% Confidence`}
                              size="small"
                              sx={{ 
                                backgroundColor: getConfidenceColor(analysisResult.confidence),
                                color: '#fff',
                                fontWeight: 'bold'
                              }}
                            />
                            <Chip 
                              label={analysisResult.sentiment.toUpperCase()}
                              size="small"
                              icon={getSentimentIcon(analysisResult.sentiment)}
                              sx={{ 
                                backgroundColor: analysisResult.sentiment === 'bullish' ? 'rgba(76, 175, 80, 0.2)' :
                                                analysisResult.sentiment === 'bearish' ? 'rgba(244, 67, 54, 0.2)' :
                                                'rgba(255, 152, 0, 0.2)',
                                color: analysisResult.sentiment === 'bullish' ? '#4caf50' :
                                       analysisResult.sentiment === 'bearish' ? '#f44336' : '#ff9800',
                                fontWeight: 'bold'
                              }}
                            />
                          </Box>
                        </Box>
                        <Typography 
                          variant="body1" 
                          color="text.primary" 
                          sx={{ 
                            mb: 3,
                            fontSize: '1.1rem',
                            lineHeight: 1.7,
                            fontWeight: 500
                          }}
                        >
                          {analysisResult.recommendation}
                        </Typography>
                        <Divider sx={{ my: 2, borderColor: 'rgba(255, 215, 0, 0.3)' }} />
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="caption" color="text.secondary">
                            Analysis generated on: {analysisResult.timestamp}
                          </Typography>
                          <Typography variant="caption" color="primary.main" sx={{ fontWeight: 600 }}>
                            Chart Analysis
                          </Typography>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </Paper>
            </motion.div>
          )}

          {/* Disclaimer */}
          <Alert severity="warning" sx={{ mt: 4 }}>
            <strong>Disclaimer:</strong> This AI analysis is for educational purposes only and should not be considered as financial advice. 
            Always conduct your own research and risk management before making trading decisions. Past performance does not guarantee future results.
          </Alert>
        </motion.div>
      </Container>
    </Box>
  );
};

export default function AIAnalyses() {
  return (
    <FeatureGuard featureId="ai_analyses">
      <AIAnalysesPage />
    </FeatureGuard>
  );
}

// Provide named export for compatibility with existing imports
export { AIAnalyses }; 